import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

/**
 * Parse a full phone number into extension and local number parts
 * @param {string} fullNumber - The complete phone number (e.g., "+6281234567")
 * @param {string} defaultExtension - Default extension if parsing fails (e.g., "+65")
 * @returns {Object} { extension: string, localNumber: string }
 */
export const parseEmergencyNumber = (fullNumber, defaultExtension = "+65") => {
  let emergencyPhoneExt = defaultExtension;
  let emergencyNum = "";

  if (!fullNumber) {
    return { extension: emergencyPhoneExt, localNumber: emergencyNum };
  }

  try {
    let cleanedNumber = fullNumber.trim();

    // guard: Add leading "+" if missing
    if (!cleanedNumber.startsWith("+")) {
      cleanedNumber = "+" + cleanedNumber;
    }

    // Parse using libphonenumber-js
    const phoneNumber = parsePhoneNumber(cleanedNumber);
    if (phoneNumber) {
      emergencyPhoneExt = "+" + phoneNumber.countryCallingCode;
      emergencyNum = phoneNumber.nationalNumber;
    } else {
      // Fallback to regex if parsing fails
      const match = cleanedNumber.match(/^(\+\d{1,4})(.+)$/);
      if (match) {
        emergencyPhoneExt = match[1];
        emergencyNum = match[2].trim();
      } else {
        emergencyNum = cleanedNumber;
      }
    }
  } catch (error) {
    // If parsing fails, fallback to regex
    let cleanedNumber = fullNumber.trim();
    if (!cleanedNumber.startsWith("+")) {
      cleanedNumber = "+" + cleanedNumber;
    }
    const match = cleanedNumber.match(/^(\+\d{1,4})(.+)$/);
    if (match) {
      emergencyPhoneExt = match[1];
      emergencyNum = match[2].trim();
    } else {
      emergencyNum = cleanedNumber;
    }
  }

  return { extension: emergencyPhoneExt, localNumber: emergencyNum };
};

/**
 * Validate a phone number using libphonenumber-js
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} true if valid, false otherwise
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return false;
  }

  try {
    // Ensure phone number has leading "+" for validation
    let fullNumber = phoneNumber.trim();
    if (!fullNumber.startsWith("+")) {
      fullNumber = "+" + fullNumber;
    }

    return isValidPhoneNumber(fullNumber);
  } catch (error) {
    return false;
  }
};

/**
 * Check if emergency number matches user's phone number (normalized comparison)
 * @param {string} emergencyNum - The emergency contact number (full or partial)
 * @param {string} userPhoneExt - User's phone extension (e.g., "+65")
 * @param {string} userPhoneNum - User's phone number (local part)
 * @returns {boolean} true if they match, false otherwise
 */
export const checkEmergencyMatchesUser = (emergencyNum, userPhoneExt, userPhoneNum) => {
  if (!emergencyNum || !userPhoneNum) {
    return false;
  }

  // Construct user's full phone number
  const userFullNumber = `${userPhoneExt || ""}${userPhoneNum}`;

  // Normalize numbers for comparison (remove all non-digit characters)
  const emergencyNormalized = emergencyNum.replace(/\D/g, "");
  const userPhoneNormalized = userFullNumber.replace(/\D/g, "");

  // Check if normalized numbers match
  return emergencyNormalized === userPhoneNormalized;
};

/**
 * Combine phone extension and local number into E.164 format
 * @param {string} phoneExtension - Phone extension (e.g., "+65")
 * @param {string} phoneNumber - Local phone number
 * @returns {string} Combined phone number in E.164 format or empty string
 */
export const combinePhoneNumber = (phoneExtension, phoneNumber) => {
  if (!phoneExtension || !phoneNumber) {
    if (phoneNumber) return phoneNumber;
    return "";
  }

  try {
    // Remove any leading "+" from local number (in case user added it)
    const cleanLocalNumber = phoneNumber.replace(/^\+/, "");
    const fullNumber = `${phoneExtension}${cleanLocalNumber}`;

    // Validate and format using libphonenumber-js
    if (isValidPhoneNumber(fullNumber)) {
      const parsedNumber = parsePhoneNumber(fullNumber);
      return parsedNumber.number; // E.164 format (e.g., "+6281285394430")
    } else {
      // If validation fails, use the combined number as-is
      return fullNumber;
    }
  } catch (error) {
    // Fallback: combine manually if libphonenumber-js fails
    const cleanLocalNumber = phoneNumber.replace(/^\+/, "");
    return `${phoneExtension}${cleanLocalNumber}`;
  }
};
